import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import {
  useSetClientRemindersMutation,
  type ModelClientReminder,
  type SetClientRemindersRequest,
} from '@app/api/ClientReminders';
import { SUCCESS, SUCCESS_MESSAGE } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type {
  SettingUpAutomaticMessagingToClientsForm,
  UseSettingUpAutomaticMessagingToClientsProps,
} from '@app/pages/Notifications/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import type { NRecordReminders } from '@app/types';

import type { BaseOptionType } from 'antd/es/select';

export const useSettingUpAutomaticMessagingToClients = ({
  data,
}: UseSettingUpAutomaticMessagingToClientsProps) => {
  const [defaultIntegrations, setDefaultIntegrations] = useState<
    NRecordReminders.ModelBranchRecordReminderIntegration[]
  >([]);
  const [integrations, setIntegrations] = useState<BaseOptionType[]>([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [sendByLastActiveIntegration, setSendByLastActiveIntegration] =
    useState(false);

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [form] = Form.useForm();

  const [setClientReminders, { isLoading: isSetClientRemindersLoading }] =
    useSetClientRemindersMutation();

  const [beforeBirthdayReminder, setBeforeBirthdayReminder] =
    useState<ModelClientReminder | null>(null);
  const [afterBirthdayReminder, setAfterBirthdayReminder] =
    useState<ModelClientReminder | null>(null);

  const convertMinutes = useCallback((totalMinutes: number) => {
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    return { days, hours, minutes };
  }, []);

  useEffect(() => {
    const fetchInitialBoxData = () => {
      if (data) {
        setDefaultIntegrations(data.DefaultIntegrations || []);
        setSendToAll(data.SendByAllIntegrations);
        setSendByLastActiveIntegration(data.SendByLastActiveIntegration);

        const beforeReminder = data.ClientReminders?.find(
          (item) => item.ReminderType === 'before-birthday'
        );
        const afterReminder = data.ClientReminders?.find(
          (item) => item.ReminderType === 'after-birthday'
        );

        setBeforeBirthdayReminder(beforeReminder ?? null);
        setAfterBirthdayReminder(afterReminder ?? null);

        form.setFieldsValue({
          beforeBirthdayStatus: beforeReminder?.IsOn,
          beforeBirthdayDays: convertMinutes(
            beforeReminder?.ReminderMinutes || 0
          ).days,
          beforeBirthdayHours: convertMinutes(
            beforeReminder?.ReminderMinutes || 0
          ).hours,
          beforeBirthdayMinutes: convertMinutes(
            beforeReminder?.ReminderMinutes || 0
          ).minutes,
          beforeBirthdayMessage: beforeReminder?.Text,
          afterBirthdayStatus: afterReminder?.IsOn,
          afterBirthdayDays: convertMinutes(afterReminder?.ReminderMinutes || 0)
            .days,
          afterBirthdayHours: convertMinutes(
            afterReminder?.ReminderMinutes || 0
          ).hours,
          afterBirthdayMinutes: convertMinutes(
            afterReminder?.ReminderMinutes || 0
          ).minutes,
          afterBirthdayMessage: afterReminder?.Text,
        });
      }
    };

    fetchInitialBoxData();
  }, [convertMinutes, data, form]);

  const handleSave = useCallback(
    async (values: SettingUpAutomaticMessagingToClientsForm) => {
      const adaptedRemindersData = ['before', 'after']
        .map((prefix) =>
          Object.entries(values).reduce(
            (acc, [key, value]) => {
              if (key.startsWith(prefix)) {
                const suffix = key.slice(prefix.length + 'Birthday'.length);
                const normalizedKey =
                  suffix.charAt(0).toLowerCase() + suffix.slice(1);

                if (key.endsWith('Days')) {
                  acc._minutes =
                    (acc._minutes || 0) + (value as number) * 24 * 60;
                } else if (key.endsWith('Hours')) {
                  acc._minutes = (acc._minutes || 0) + (value as number) * 60;
                } else if (key.endsWith('Minutes')) {
                  acc._minutes = (acc._minutes || 0) + (value as number);
                } else {
                  acc[normalizedKey] = value;
                }
              }
              return acc;
            },
            { type: prefix } as unknown as Record<string, number>
          )
        )
        .map((item) => {
          if (item._minutes !== undefined) {
            item.totalMinutes = item._minutes;
            delete item._minutes;
          }
          return item;
        });

      try {
        const data: SetClientRemindersRequest['data'] = {
          AllowedIntegrations: null,
          DefaultIntegrations: defaultIntegrations,
          SendByAllIntegrations: sendToAll,
          SendByLastActiveIntegration: sendByLastActiveIntegration,
          ClientReminders: adaptedRemindersData.map((item) => {
            const isBefore = String(item.type) === 'before';

            const existingReminder = isBefore
              ? beforeBirthdayReminder
              : afterBirthdayReminder;

            const existingFileIds = existingReminder?.FileIds;

            const normalizedFileIds = Array.isArray(existingFileIds)
              ? existingFileIds
              : [];

            return {
              Id: existingReminder?.Id ?? 0,
              IsOn: Boolean(item.status),
              ReminderType: `${String(item.type)}-birthday`,
              ReminderMinutes: Number(item.totalMinutes ?? 0),
              Text: typeof item.message === 'string' ? item.message : null,
              FileIds: normalizedFileIds,
              SendByAllIntegrations:
                existingReminder?.SendByAllIntegrations ?? false,
            };
          }),
        };

        await setClientReminders({
          ActiveBranchId,
          data,
        }).unwrap();

        showNotification(SUCCESS_MESSAGE, SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      ActiveBranchId,
      afterBirthdayReminder,
      beforeBirthdayReminder,
      defaultIntegrations,
      sendByLastActiveIntegration,
      sendToAll,
      setClientReminders,
      showNotification,
    ]
  );

  return {
    form,
    integrations,
    setIntegrations,
    handleSave,
    isSetClientRemindersLoading,
    beforeBirthdayReminder,
    afterBirthdayReminder,
    defaultIntegrations,
    setDefaultIntegrations,
    sendToAll,
    setSendToAll,
    sendByLastActiveIntegration,
    setSendByLastActiveIntegration,
  };
};
